import { CheckboxItemNode } from './checkbox-tree.model';

export class Activity {
  id: string;
  title: string;
  exercises?: Exercise[];
  step: Step;
  typology: Typology;
  categoryId: string;
  groupEvaluationStatus: GroupEvaluationStatus;
  isSolutionaryAvailable: boolean;
  isExtra: boolean;
  itineraryId?: string;
}

export class Exercise {
  id: string;
  order: number;
  statement: string;
  answers: Answers[];
  hasTeacherGuide?: boolean;
  hasSolution?: boolean;
  teacherGuide?: string;
  solutionary?: string;
  isHidden: boolean;
  isEvaluable?: boolean;
  isEvaluation: boolean;
}

export class ExerciseTeacherGuide {
  teacherGuide: string;
}

export class ExerciseSolutionary {
  solutionary: string;
}

export class Answers {
  id: string;
  value?: boolean;
  text: string;
  isCorrect?: boolean;
  order?: number;
}

export class Step {
  id: string;
  title: string;
  stretch: Stretch;
}

export class Typology {
  title: string;
  styleId: string;
  name: string;
}

export class Stretch {
  styleId: string;
  title: string;
}

export class Rubric {
  subCompetenceId: string;
  competenceTitle: string;
  evaluationItemTitle: string;
  evaluationItemSubTitle: string;
  rubricLevels: RubricLevels[];
  grade?: number | string;
}

export class RubricsAnswers {
  totalQuestions: number;
  correctQuestions: number;
  tries: number;
  levels: Range[];
}

export interface Range {
  name?: string;
  min: number;
  max: number;
}

export class RubricLevels {
  description: string;
  range: Range;
}

export class Grades {
  subCompetenceId: string;
  grade: number | string;
}

export class Tags {
  name: string;
  value?: boolean;
  customizedStudents?: Student[];
  customize?: CheckboxItemNode[];
  isEditable?: boolean;
}

export class Status {
  state: number;
}

export class ActivityStudentAnswersStatus {
  state: number;
  exercisesAnswers: ActivityStudentAnswers[];
}

export interface StudentsStatus {
  sent: boolean;
  pending: boolean;
}

export interface GroupEvaluationStatus {
  studentsStatus: StudentSubcompetencesEvaluationStatus[];
  countDelivered: number;
  countPending: number;
}

export class StudentEvaluationStatus {
  studentId?: string;
  state?: number;
  isSent?: boolean;
}

export class StudentSubcompetences {
  studentId?: string;
  subCompetenceGrade?: Grades[];
}

export class StudentSubcompetencesEvaluationStatus extends StudentEvaluationStatus {
  id: string;
  name: string;
  activityStatus?: number;
  grade1?: string | number;
  grade2?: string | number;
  sentAt?: Date | string;
  subCompetenceGrade?: Grades[];

  constructor(
    id: string,
    name: string,
    activityStatus: number,
    grade1: string | number,
    grade2: string | number,
    subCompetenceGrade?: Grades[]
  ) {
    super();
    this.id = id;
    this.name = name;
    this.activityStatus = activityStatus;
    this.grade1 = grade1;
    this.grade2 = grade2;
    this.subCompetenceGrade = subCompetenceGrade;
  }
}

export interface SubCompetencesAssessmentStudents extends Grades {
  students: string[];
}

export interface Feature extends Tags {
  name: string;
  title?: string;
  visible?: boolean;
  active?: boolean;
  disable?: boolean;
  help?: string;
  customize?: CheckboxItemNode[];
  amountCustom?: number;
}

export interface ActivityStudentFeature {
  name: string;
  students: Student[];
}

export interface Student {
  id: string;
  value: boolean;
}

export interface ActivityStudentAverage {
  averageGrade?: number;
  exercises?: ExerciseAverage[];
}

export interface ExerciseAverage {
  exerciseId?: string;
  totalInputs?: number;
  correctInputs?: number;
  averageGrade?: number;
}

export interface ActivityStudent {
  activity: Activity;
  studentAnswers: ActivityStudentAnswers;
}

export interface ActivityStudentAnswers {
  exerciseId: string;
  inputs?: InputAnswer[];
  multipleAnswers?: StudentMultiAnswers[];
  isMultipleAnswerSelectionCorrect?: boolean;
  lastUpdate?: string;
  sentAt?: Date | string;
}

export interface InputAnswer {
  order: number;
  value: string;
  isCorrect?: boolean;
  hasExpectedAnswer?: boolean;
}

export interface StudentMultiAnswers {
  answerId: string;
  selected: boolean;
  isCorrect?: boolean;
  order?: number;
}

export interface MultiAnswers {
  answerId: string;
  correct: boolean;
}

export interface LocalStorageActivityStudentAnswers {
  studentId: string;
  date: string;
  activityId: string;
  answers: ActivityStudentAnswers[];
}

export interface UpdatedActivityFeature {
  name: string;
  value: boolean;
}

export interface TeachingInfo {
  description: string;
  contents: string[];
  intelligences: string[];
  competences: Competence[];
  relatedSteps: string[];
}

export interface Competence {
  title: string[];
  dimension: string[];
  subcompetence: string[];
}

export interface Resources {
  video: Resource[];
  resources: Resource[];
}

export interface Resource {
  title: string;
  url: string;
  activityId: string;
  exercises: ExerciseResource[];
}

export interface ExerciseResource {
  exerciseId: string;
  subsections: ExerciseResource[];
  title: string;
}

export interface SelfAssessment {
  fileName: string;
  url: string;
}

export class Materials {
  suggested: SuggestedMaterials;
}

export interface SuggestedMaterials {
  title: string;
  content: string;
}

export interface EvaluationPage {
  exerciseId: string;
  isEvaluable: boolean;
}

export interface AverageGrade {
  exerciseId?: string;
  groupAverageGrade?: number | string;
}

//ENUMS
export enum ActivityStatus {
  PENDING = 0,
  SENT = 1,
  ASSESSED = 2,
  TRIES = 3,
}

export enum ActivityTypologyName {
  THEORETICAL_RESOURCE = 'theoretical-resource',
  PISA_TEST = 'pisa-test',
  DOWNLOADABLE_EXERCISE = 'downloadable-exercise',
}

export enum ActivityStretchStyle {
  EXTRA = 'extra',
}

export enum FeatureName {
  IS_EVALUABLE = 'isEvaluable',
  IS_SOLUTIONARY = 'isSolutionaryAvailable',
  IS_HIDDEN = 'isHidden',
  IS_SELFCORRECTING = 'IsSelfCorrecting',
}

export class ActivityLoaded {
  [key: string]: boolean;
}
